import { EmptyValue } from "@bcc/components/ui/empty-value";
import { useTaxRatesContext } from "@bcc/contexts/tax-rates-context";
import { isPriceUpdateDisabledForRegion } from "@bcc/lib/products/price-update-region";
import { resolveShipToPrice, resolveShipToWholesalePrice } from "@bcc/lib/products/ship-to-countries";
import { formatPrice, parsePrice } from "@bcc/lib/utils";
import { useConfigState } from "@client-shared/providers/config";
import { useProductsState } from "@client-shared/providers/products";
import { VendorContext } from "@client-shared/providers/vendor";
import type { RoasterApprovalRequestDraft } from "@shared/services/salesforce/types";
import { CellContext } from "@tanstack/react-table";
import { Pencil } from "lucide-react";
import { useContext } from "react";
import type { PriceUpdateVariant } from "../../price-update-popover";
import { PriceUpdatePopover } from "../../price-update-popover";
import { homeRegionForVendor, setMsrpRrpCellTestId, setMsrpRrpEditButtonTestId } from "../products-table.utils";
import { VariantWithProductData } from "../types";

interface MsrpRrpCellProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const MsrpRrpCellMarkup = (props: CellContext<VariantWithProductData, unknown> & MsrpRrpCellProps) => {
  const data = props.row.original;
  const [{ currencyObject, currentVendor }] = useContext(VendorContext);
  const [{ config }] = useConfigState();
  const [, , productsProviderMethods] = useProductsState();
  const ratesByRegion = useTaxRatesContext();

  const homeRegion = homeRegionForVendor(currentVendor?.countryCode, currentVendor?.region);
  // The multi-channel PIM fetch carries per-country prices in `shipTo` only and
  // leaves the variant's top-level price / wholesalePrice empty. Fall back to the
  // home-region ship-to entry (then the first) so the RRP/MSRP column and the
  // price-update popover show real values instead of a dash.
  const homeShipTo = data.shipTo?.find((country) => country.countryCode === homeRegion) ?? data.shipTo?.[0];
  // The logged-in country's per-channel `shipToInfo` price wins: on PIM-first
  // rows it is the only value an approved Price Update reaches, while the flat
  // `data.price` comes from the Salesforce overlay and can be stale.
  const basePrice = resolveShipToPrice(data.shipToInfo, currentVendor?.countryCode, data.price ?? homeShipTo?.retailPrice);
  const baseWholesale = resolveShipToWholesalePrice(
    data.shipToInfo,
    currentVendor?.countryCode,
    data.wholesalePrice ?? homeShipTo?.wholesalePrice
  );

  const formatted = formatPrice(basePrice, currencyObject?.symbol, currencyObject?.iso);

  if (!data.skuCode) {
    return (
      <span className="text-sm font-medium" data-testid={setMsrpRrpCellTestId(props.row.index)}>
        {formatted || <EmptyValue />}
      </span>
    );
  }

  if (
    !currentVendor?.vendorKey ||
    !productsProviderMethods ||
    data.hasPendingPriceApproval ||
    isPriceUpdateDisabledForRegion(currentVendor.region) ||
    config.actionsConfig?.PriceUpdateActionAllowed === false
  ) {
    return (
      <span className="text-sm font-medium" data-testid={setMsrpRrpCellTestId(props.row.index)}>
        {formatted}
      </span>
    );
  }

  const variant: PriceUpdateVariant = {
    id: data.id,
    size: data.bagSize ?? "",
    sku: data.skuCode,
    vendorKey: currentVendor.vendorKey,
    productName: data.productTitle ?? "",
    coffeeTerm: data.term === "Short" ? "Short" : "Long",
    msrp: parsePrice(basePrice),
    wholesalePrice: parsePrice(baseWholesale),
    // Roaster's own country drives roasterCountryCode and the single ship-to
    // entry for non-cross-border SKUs.
    roasterCountryCode: currentVendor.countryCode ?? homeRegion ?? "",
    // Existing weekly inventory the backend requires on each price-update entry.
    weeklyInventory: data.weeklyInventory ?? data.maxBagsShippedPerWeek,
    // Per-country current prices for cross-border SKUs (channel → country).
    shipToInfo: data.shipToInfo,
    // Cross-border: drives the home-region-editable / others-derived popover
    // layout. Empty until the product fetch populates per-country ship-to data.
    shipTo: data.shipTo?.map((country) => country.countryCode),
    homeRegion,
  };

  const handleUpdatePrice = (request: RoasterApprovalRequestDraft) => productsProviderMethods.handleUpdateSkuStatus([request]);

  return (
    <div data-testid={setMsrpRrpCellTestId(props.row.index)}>
      <PriceUpdatePopover
        variant={variant}
        onUpdatePrice={handleUpdatePrice}
        isUpdating={productsProviderMethods.isUpdatingSkuStatus}
        ratesByRegion={ratesByRegion}
        marginFloorPercent={config.marginConfig?.marginPercentage}
        open={props.open}
        onOpenChange={props.onOpenChange}
      >
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline cursor-pointer"
          data-testid={setMsrpRrpEditButtonTestId(props.row.index)}
        >
          {formatted}
          <Pencil className="h-3 w-3" />
        </button>
      </PriceUpdatePopover>
    </div>
  );
};
